import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../context/AuthContext';
import useHousehold from '../hooks/useHousehold';
import { authFetch } from '../lib/authFetch';

const ProfilePage: React.FC = () => {
  const { auth, refreshUser } = useAuth();
  const household = useHousehold();
  const [isEditing, setIsEditing] = useState(false);
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const hasUser = Boolean(auth.user);
  const createdDateRaw = (auth.user as { createdAt?: string } | null)?.createdAt ?? '';

  useEffect(() => {
    document.title = 'Account | Velora Money';
  }, []);

  const firstName = auth.user?.firstName?.trim() ?? '';
  const lastName = auth.user?.lastName?.trim() ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  const email = auth.user?.email ?? '—';
  const displayName = fullName || email || '—';
  const role = auth.user?.role ?? '—';
  const onboardingStatus =
    auth.user?.hasCompletedOnboarding === true
      ? 'Completed'
      : auth.user?.hasCompletedOnboarding === false
        ? 'Not completed'
        : '—';
  const householdRole = useMemo(
    () => household.members.find((member) => member.userId === auth.user?.id)?.roleInHousehold ?? null,
    [auth.user?.id, household.members]
  );
  const canEditDisplayName = householdRole
    ? householdRole === 'primary' || householdRole === 'partner'
    : auth.user?.role === 'primary' || auth.user?.role === 'partner';

  const createdDate = useMemo(() => {
    if (!createdDateRaw) return '—';
    const parsed = new Date(createdDateRaw);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(parsed);
  }, [createdDateRaw]);

  const renderValue = (value: string) => (hasUser || !auth.loading ? value : <Skeleton width={220} />);

  const initializeDrafts = () => {
    setDraftFirstName((auth.user?.firstName ?? '').trim().slice(0, 50));
    setDraftLastName((auth.user?.lastName ?? '').trim().slice(0, 50));
  };

  const handleEdit = () => {
    if (!hasUser || !canEditDisplayName) return;
    initializeDrafts();
    setSaveError('');
    setSaveSuccess('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    initializeDrafts();
    setIsEditing(false);
  };

  const handleFirstNameChange = (value: string) => {
    setDraftFirstName(value.trimStart().slice(0, 50));
  };

  const handleLastNameChange = (value: string) => {
    setDraftLastName(value.trimStart().slice(0, 50));
  };

  const handleSave = async () => {
    if (!auth.token || !hasUser) return;

    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      const payload = {
        firstName: draftFirstName.trim().slice(0, 50),
        lastName: draftLastName.trim().slice(0, 50),
      };

      const res = await authFetch('/api/users/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || 'Unable to save profile changes');
      }

      await refreshUser();
      setIsEditing(false);
      setSaveSuccess('Profile updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save profile changes';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Profile & Access
      </Typography>

      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Identity
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Full name:</strong> {renderValue(displayName)}
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Email:</strong> {renderValue(email)}
          </Typography>
          <Typography>
            <strong>Role:</strong> {renderValue(role)}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Role note:</strong> Assigned by an admin.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Onboarding status:</strong> {renderValue(onboardingStatus)}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            <strong>Created date:</strong> {renderValue(createdDate)}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Household
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Household name:</strong> {household.household?.name?.trim() || '—'}
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Household ID:</strong> {household.household?.id || '—'}
          </Typography>
          <Typography sx={{ mb: 1 }}>
            <strong>Your role in household:</strong> {householdRole || '—'}
          </Typography>

          {household.loading ? (
            <Typography color="text.secondary">Loading household details...</Typography>
          ) : household.error ? (
            <Typography color="error">{household.error}</Typography>
          ) : household.members.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Members
              </Typography>
              <List dense disablePadding>
                {household.members.map((member) => {
                  const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email;
                  return (
                    <ListItem key={member.userId} disableGutters>
                      <ListItemText primary={memberName} secondary={`${member.email} · ${member.roleInHousehold}`} />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ) : (
            <Typography color="text.secondary">No household members found.</Typography>
          )}
        </Paper>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Advanced: Display name
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ mb: 2 }}>
              Only first and last name are editable here. Role and household assignment cannot be changed on this page.
            </Typography>
            {!isEditing ? (
              <Button variant="outlined" onClick={handleEdit} disabled={!hasUser || !canEditDisplayName}>
                Edit display name
              </Button>
            ) : (
              <Stack spacing={2} sx={{ maxWidth: 420 }}>
                <TextField
                  label="First name"
                  value={draftFirstName}
                  onChange={(event) => handleFirstNameChange(event.target.value)}
                  onBlur={(event) => setDraftFirstName(event.target.value.trim().slice(0, 50))}
                  inputProps={{ maxLength: 50 }}
                  helperText="Up to 50 characters."
                />
                <TextField
                  label="Last name"
                  value={draftLastName}
                  onChange={(event) => handleLastNameChange(event.target.value)}
                  onBlur={(event) => setDraftLastName(event.target.value.trim().slice(0, 50))}
                  inputProps={{ maxLength: 50 }}
                  helperText="Up to 50 characters."
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSave} disabled={isSaving || !auth.token || !hasUser}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Stack>
                {saveError ? <Typography color="error">{saveError}</Typography> : null}
              </Stack>
            )}

            {!canEditDisplayName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Display name can only be edited by primary or partner household members.
              </Typography>
            ) : null}
            {!isEditing && saveSuccess ? (
              <Typography color="success.main" sx={{ mt: 2 }}>
                Profile updated.
              </Typography>
            ) : null}
          </AccordionDetails>
        </Accordion>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Permissions explainer
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters>
              <ListItemText primary="Shared accounts are visible to household members." />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="Private accounts are only visible to you." />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText primary="Transactions visibility follows the account’s access scope." />
            </ListItem>
          </List>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ProfilePage;
